import React, { useEffect, useMemo, useState } from "react";
import { UploadCloud, HardDrive, Trash2, Download as DownloadIcon, File as FileIcon, Image as ImageIcon, FileText, Film, Music, Package, Loader2, Share2, Copy, Link2Off, QrCode } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const fmtBytes = (n) => {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const rupees = (paise) => `₹${((paise || 0) / 100).toFixed(2)}`;

// Iconify by MIME family so a folder scan reads at a glance.
function TypeIcon({ contentType, className = "" }) {
  const t = (contentType || "").toLowerCase();
  const cls = `w-5 h-5 ${className}`;
  if (t.startsWith("image/")) return <ImageIcon className={`${cls} text-fuchsia-500`} />;
  if (t.startsWith("video/")) return <Film className={`${cls} text-sky-500`} />;
  if (t.startsWith("audio/")) return <Music className={`${cls} text-emerald-500`} />;
  if (t.includes("pdf") || t.includes("text") || t.includes("word") || t.includes("document")) return <FileText className={`${cls} text-orange-500`} />;
  if (t.includes("zip") || t.includes("compressed")) return <Package className={`${cls} text-amber-600`} />;
  return <FileIcon className={`${cls} text-stone-500`} />;
}

export default function Files() {
  const { user } = useAuth();
  const [quota, setQuota] = useState(null);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");
  const [drag, setDrag] = useState(false);

  useSeo({
    title: "My Files · find2near",
    description: "Upload, download and share files. 20 MB free per account · ₹1 per MB after.",
    canonical: "/files",
  });

  const refresh = async () => {
    const [q, l] = await Promise.all([api.get("/files/quota"), api.get("/files/mine")]);
    setQuota(q.data);
    setFiles(l.data);
  };

  useEffect(() => {
    if (!user) return;
    refresh().catch(() => {});
  }, [user]);

  // Estimate the overage the user would pay for a specific file — shown as a
  // pre-flight so nobody is surprised by a wallet debit after the fact.
  const estimateOverage = (bytes) => {
    if (!quota) return 0;
    const end = quota.used_bytes + bytes;
    if (end <= quota.free_bytes) return 0;
    const billable = end - Math.max(quota.used_bytes, quota.free_bytes);
    return Math.ceil(billable / (1024 * 1024)) * quota.paise_per_mb;
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > (quota?.max_single_upload_bytes || 200 * 1024 * 1024)) {
      toast.error(`Max ${fmtBytes(quota.max_single_upload_bytes)} per file.`);
      return;
    }
    const overage = estimateOverage(file.size);
    if (overage > 0 && (quota?.wallet_available_paise || 0) < overage) {
      toast.error(`Need ${rupees(overage)} to store this file. Top up your wallet first.`);
      return;
    }
    if (overage > 0) {
      const ok = window.confirm(
        `This file will use ${fmtBytes(file.size)} of storage. ` +
        `Your free 20 MB is used up, so ${rupees(overage)} will be debited from your wallet. Continue?`
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (label.trim()) form.append("label", label.trim());
      const r = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(
        r.data.billed
          ? `Uploaded · ${rupees(r.data.charge_paise)} debited from wallet.`
          : "Uploaded to free tier."
      );
      setLabel("");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (fileId) => {
    if (!window.confirm("Delete this file? This frees your quota but is irreversible.")) return;
    try {
      await api.delete(`/files/${fileId}`);
      toast.success("File deleted");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // Public share link — anyone with the URL can download (until revoked).
  const [shareBusy, setShareBusy] = useState(null);
  const [expandedShares, setExpandedShares] = useState({});
  const [sharesByFile, setSharesByFile] = useState({});
  const [qrShareId, setQrShareId] = useState(null);

  const toggleShares = async (fileId) => {
    const next = !expandedShares[fileId];
    setExpandedShares({ ...expandedShares, [fileId]: next });
    if (next && !sharesByFile[fileId]) {
      try {
        const r = await api.get(`/files/${fileId}/shares`);
        setSharesByFile({ ...sharesByFile, [fileId]: r.data });
      } catch {}
    }
  };

  const createShare = async (fileId, expiresHours) => {
    setShareBusy(fileId);
    try {
      const r = await api.post(`/files/${fileId}/share`, { expires_hours: expiresHours || null });
      const shareUrl = `${window.location.origin}${r.data.url_path}`;
      const listR = await api.get(`/files/${fileId}/shares`);
      setSharesByFile((s) => ({ ...s, [fileId]: listR.data }));
      setExpandedShares((s) => ({ ...s, [fileId]: true }));
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Share link copied to clipboard");
      } catch {
        toast.success("Share link created");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create link");
    } finally {
      setShareBusy(null);
    }
  };

  const revokeShare = async (fileId, shareId) => {
    try {
      await api.delete(`/files/share/${shareId}`);
      const r = await api.get(`/files/${fileId}/shares`);
      setSharesByFile((s) => ({ ...s, [fileId]: r.data }));
      toast.success("Link revoked");
    } catch (e) {
      toast.error("Could not revoke");
    }
  };

  const copyLink = async (shareId) => {
    const url = `${window.location.origin}/f/${shareId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — long-press to copy manually");
    }
  };

  const usedPct = useMemo(() => {
    if (!quota) return 0;
    return Math.min(100, Math.round((quota.used_bytes / quota.free_bytes) * 100));
  }, [quota]);

  const overFree = quota && quota.used_bytes > quota.free_bytes;

  if (!user) {
    return (
      <div className="max-w-md mx-auto text-center py-24 px-6">
        <UploadCloud className="mx-auto text-[#E05E36]" size={48} />
        <h2 className="heading text-2xl font-extrabold mt-4">Sign in to store your files</h2>
        <p className="text-stone-500 mt-2 text-sm">20 MB free · ₹1 per MB after.</p>
        <Link to="/onboarding" className="inline-block mt-6 bg-[#E05E36] text-white px-6 py-3 rounded-full font-bold" data-testid="files-signin">Get started</Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8" data-testid="files-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">My Storage</div>
        <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900">Files</h1>
        <p className="text-stone-500 text-sm mt-1">
          Upload photos, docs, videos — accessible from any device. First 20 MB free, then ₹1 per MB.
        </p>
      </div>

      {/* Quota strip */}
      <div className="rounded-3xl border border-stone-200 bg-white p-5 md:p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-xl grid place-items-center ${overFree ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
              <HardDrive size={22} />
            </div>
            <div>
              <div className="heading text-lg font-extrabold text-stone-900">
                {fmtBytes(quota?.used_bytes || 0)} <span className="text-stone-400 font-medium">/ {fmtBytes(quota?.free_bytes || 0)} free</span>
              </div>
              <div className="text-xs text-stone-500">
                {overFree
                  ? `Over free tier · ${rupees(quota?.wallet_available_paise || 0)} wallet available`
                  : `${fmtBytes(quota?.remaining_free_bytes || 0)} of free storage remaining`}
              </div>
            </div>
          </div>
          <Link to="/hub#wallet" className="text-xs font-bold text-[#E05E36] hover:underline" data-testid="files-topup">
            Top up wallet →
          </Link>
        </div>
        <div className="mt-4 h-2 rounded-full bg-stone-100 overflow-hidden">
          <div
            className={`h-full transition-all ${overFree ? "bg-amber-500" : "bg-emerald-500"}`}
            style={{ width: `${usedPct}%` }}
            data-testid="files-usage-bar"
          />
        </div>
      </div>

      {/* Upload dropzone */}
      <label
        onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          const f = e.dataTransfer.files?.[0];
          if (f) uploadFile(f);
        }}
        className={`mt-6 block rounded-3xl border-2 border-dashed p-8 md:p-10 text-center cursor-pointer transition-colors ${drag ? "border-[#E05E36] bg-[#FFF3EC]" : "border-stone-300 bg-white hover:border-[#E05E36] hover:bg-[#FFF3EC]/40"}`}
        data-testid="files-dropzone"
      >
        <UploadCloud className={`mx-auto mb-2 ${drag ? "text-[#E05E36]" : "text-stone-400"}`} size={36} />
        <div className="heading font-extrabold text-stone-900">Drop a file here or click to browse</div>
        <div className="text-xs text-stone-500 mt-1">Max {fmtBytes(quota?.max_single_upload_bytes || 0)} per upload</div>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          placeholder="Optional label — e.g. 'Aadhaar 2024'"
          className="mt-4 w-full max-w-sm mx-auto block px-3 py-2 rounded-full bg-white border border-stone-200 text-sm outline-none focus:border-[#E05E36]"
          data-testid="files-label-input"
        />
        <input
          type="file"
          className="hidden"
          disabled={busy}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) uploadFile(f);
            e.target.value = "";
          }}
          data-testid="files-input"
        />
        {busy && (
          <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#E05E36] font-bold">
            <Loader2 size={16} className="animate-spin" /> Uploading…
          </div>
        )}
      </label>

      {/* File list */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="heading text-lg font-extrabold text-stone-900">Your files ({files.length})</h2>
        </div>
        {files.length === 0 ? (
          <div className="text-center text-sm text-stone-500 py-14 bg-white rounded-2xl border border-stone-200">
            No files yet. Drop one above to get started.
          </div>
        ) : (
          <ul className="grid gap-3">
            {files.map((f) => (
              <li
                key={f.file_id}
                className="rounded-2xl border border-stone-200 bg-white p-4"
                data-testid={`file-${f.file_id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-stone-100 grid place-items-center shrink-0">
                    <TypeIcon contentType={f.content_type} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-stone-900 truncate">{f.label || f.original_filename}</div>
                    <div className="text-xs text-stone-500 truncate">
                      {f.original_filename} · {fmtBytes(f.size)} · {new Date(f.created_at).toLocaleString()}
                    </div>
                  </div>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.file_id}/download`}
                    className="text-stone-600 hover:text-[#E05E36]"
                    title="Download"
                    data-testid={`file-download-${f.file_id}`}
                  >
                    <DownloadIcon size={18} />
                  </a>
                  <button
                    onClick={() => toggleShares(f.file_id)}
                    className={`hover:text-sky-600 ${expandedShares[f.file_id] ? "text-sky-600" : "text-stone-600"}`}
                    title="Share"
                    data-testid={`file-share-${f.file_id}`}
                  >
                    <Share2 size={18} />
                  </button>
                  <button
                    onClick={() => onDelete(f.file_id)}
                    className="text-stone-600 hover:text-red-600"
                    title="Delete"
                    data-testid={`file-delete-${f.file_id}`}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                {expandedShares[f.file_id] && (
                  <div className="mt-3 pt-3 border-t border-stone-100" data-testid={`file-shares-${f.file_id}`}>
                    {qrShareId && (sharesByFile[f.file_id] || []).some(s => s.share_id === qrShareId) && (
                      <div className="mb-3 flex items-center gap-3 rounded-xl bg-fuchsia-50 border border-fuchsia-200 p-3" data-testid={`qr-modal-${qrShareId}`}>
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/qr?data=${encodeURIComponent(`${window.location.origin}/f/${qrShareId}`)}&size=180`}
                          alt="QR code"
                          className="w-32 h-32 rounded-lg bg-white p-1"
                          data-testid={`qr-image-${qrShareId}`}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-xs uppercase tracking-widest font-black text-fuchsia-700">Scan to receive</div>
                          <div className="text-sm text-stone-700 mt-1">Point another phone's camera at the QR code — the browser will download the file automatically.</div>
                          <button onClick={() => setQrShareId(null)} className="mt-2 text-xs font-bold text-stone-500 hover:text-stone-800">Hide QR</button>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="text-[10px] uppercase tracking-widest font-black text-stone-500">Create a link</span>
                      <button
                        onClick={() => createShare(f.file_id, null)}
                        disabled={shareBusy === f.file_id}
                        className="text-xs font-bold px-3 py-1.5 rounded-full bg-sky-600 hover:bg-sky-700 text-white transition-colors disabled:opacity-50"
                        data-testid={`file-share-create-forever-${f.file_id}`}
                      >
                        {shareBusy === f.file_id ? "…" : "Never expires"}
                      </button>
                      <button
                        onClick={() => createShare(f.file_id, 24)}
                        disabled={shareBusy === f.file_id}
                        className="text-xs font-bold px-3 py-1.5 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-800 transition-colors"
                        data-testid={`file-share-create-24h-${f.file_id}`}
                      >
                        24 hours
                      </button>
                      <button
                        onClick={() => createShare(f.file_id, 24 * 7)}
                        disabled={shareBusy === f.file_id}
                        className="text-xs font-bold px-3 py-1.5 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-800 transition-colors"
                        data-testid={`file-share-create-7d-${f.file_id}`}
                      >
                        7 days
                      </button>
                    </div>
                    {(sharesByFile[f.file_id] || []).length === 0 ? (
                      <div className="text-xs text-stone-500">No share links yet.</div>
                    ) : (
                      <ul className="space-y-2">
                        {sharesByFile[f.file_id].map((s) => (
                          <li key={s.share_id} className="flex items-center gap-2 text-xs bg-stone-50 rounded-lg px-3 py-2" data-testid={`share-${s.share_id}`}>
                            <code className="flex-1 truncate font-mono text-stone-700">/f/{s.share_id}</code>
                            <span className="text-stone-400">{s.download_count} dl</span>
                            {s.expires_at && (
                              <span className="text-amber-600">
                                {new Date(s.expires_at) < new Date() ? "expired" : `→ ${new Date(s.expires_at).toLocaleDateString()}`}
                              </span>
                            )}
                            <button
                              onClick={() => copyLink(s.share_id)}
                              className="text-sky-600 hover:text-sky-800"
                              title="Copy link"
                              data-testid={`share-copy-${s.share_id}`}
                            >
                              <Copy size={14} />
                            </button>
                            <button
                              onClick={() => setQrShareId(qrShareId === s.share_id ? null : s.share_id)}
                              className={`hover:text-fuchsia-800 ${qrShareId === s.share_id ? "text-fuchsia-600" : "text-stone-600"}`}
                              title="Show QR code"
                              data-testid={`share-qr-${s.share_id}`}
                            >
                              <QrCode size={14} />
                            </button>
                            <button
                              onClick={() => revokeShare(f.file_id, s.share_id)}
                              className="text-red-600 hover:text-red-800"
                              title="Revoke"
                              data-testid={`share-revoke-${s.share_id}`}
                            >
                              <Link2Off size={14} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
